/** Блок «Юнифанс» (04.11, предложение оператора, «да» владельца): одна разметка для формы отчёта
 *  (reportPlayers.tsx → UnifansSection) и карточки сданного отчёта (ReportUnifans.tsx, вкладка «Отчёты»):
 *  шапка со счётчиком «в приложении N из M», ачивки «N из M», строки игроков с ★ капитаном и тегами.
 *  Данные и сохранение — у хозяина: форма копит до «Отправить», вкладка пишет сразу. */
import type { ReactNode } from "react";
import { AchPicker, type AchItem } from "./reportFields";
import { PlayersBadge } from "../../components/shared";
import { PLAYER_TAGS, tgHandle, ufCaption, type UfCount, type UfRow } from "./unifansModel";
import m from "./opReport.module.css";
import u from "./unifansBlock.module.css";

export interface UfAch {
  items: AchItem[]; sel: Set<string>; help: Set<string>; open: boolean;
  onOpen: () => void; onSel: (id: string) => void; onHelp: (id: string) => void;
}

/** «N из 👤M»: число игроков брони — только бейджем игроков (канон 05.09). */
export function UfOf({ c }: { c: UfCount }) {
  return <span className={u.of}><b>{c.joined}</b> из <PlayersBadge count={c.expected} size="sm" /></span>;
}

/** Рамка блока: шапка «Юнифанс» со счётчиком; bar — полоска и подпись «ещё K не в приложении». */
export function UnifansHud({ count, bar, children }: { count?: UfCount | null; bar?: boolean; children: ReactNode }) {
  const cap = count && bar ? ufCaption(count) : null;
  return (
    <section className={`${m.hud} ${m.gold}`}>
      <div className={`${m.hh} ${count ? "" : m.ufh}`}>Юнифанс
        {count ? <span className={u.cnt}>в приложении <UfOf c={count} /></span> : null}
      </div>
      {count && bar && count.expected > 0 ? (
        <div className={u.bar} role="progressbar" aria-label="В приложении" aria-valuenow={count.pct} aria-valuemin={0} aria-valuemax={100}>
          <i style={{ width: `${count.pct}%` }} />
        </div>
      ) : null}
      {cap ? <div className={u.cap}>{cap}</div> : null}
      {children}
    </section>
  );
}

/** Ачивки + строки игроков. rows=null — имена скрыты (правило ПДн), строк не рисуем. */
export function UfFill({ ach, rows, onCaptain, onTag, busy }: {
  ach: UfAch; rows: UfRow[] | null; onCaptain: (i: number) => void; onTag: (i: number, t: string) => void; busy?: boolean;
}) {
  return (<>
    <AchPicker items={ach.items} sel={ach.sel} help={ach.help} open={ach.open} onOpen={ach.onOpen} onSel={ach.onSel} onHelp={ach.onHelp} />
    {/* 28.08 (владелец): ручные карточки игроков убраны — привязавшиеся через юнифанс появляются сами, оператор только выставляет теги */}
    {rows ? (
      <div style={{ marginTop: 8 }} aria-busy={busy || undefined}>
        {rows.length === 0 ? <div className={m.ufbar}>Через юнифанс пока никто не привязался</div> : null}
        {rows.map((p, i) => (
          <div key={i} className={m.ufpRow}>
            <span className={m.ufpNm}>✈ {p.name}{tgHandle(p.telegram) ? <i className={m.ufpTg}> {tgHandle(p.telegram)}</i> : null}</span>
            <span className={m.ufpTags}>
              {/* 19.09 (владелец): «капитана пусть назначает оператор в отчёте с помощью тегов» — звезда одна на игру (ОО видит её в сводке дня, ДК — в досье) */}
              <button type="button" className={`${m.tag} ${p.captain ? m.on : ""}`} title="Капитан команды" aria-pressed={p.captain} onClick={() => onCaptain(i)}>★ капитан</button>
              {PLAYER_TAGS.map((t) => (
                <button key={t} type="button" className={`${m.tag} ${p.tags.includes(t) ? m.on : ""}`} aria-pressed={p.tags.includes(t)} onClick={() => onTag(i, t)}>{t}</button>
              ))}
            </span>
          </div>
        ))}
      </div>
    ) : null}
  </>);
}
